import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDxyCloseHistory } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from "@/components/ui/select";

const RANGES = [15, 30, 60];
const INSTRUMENTS = [
  { key: "DXY", label: "DXY", digits: 2 },
  { key: "EURUSD", label: "EUR/USD", digits: 4 },
  { key: "GBPUSD", label: "GBP/USD", digits: 4 },
  { key: "USDJPY", label: "USD/JPY", digits: 2 },
  { key: "XAUUSD", label: "Gold", digits: 2 },
  { key: "WTI", label: "WTI", digits: 2 },
];

export default function DxyCloseHistory() {
  const [days, setDays] = useState(60);
  const [instrument, setInstrument] = useState("DXY");
  const inst = INSTRUMENTS.find((x) => x.key === instrument) || INSTRUMENTS[0];
  const fmt = (v) => Number(v).toFixed(inst.digits);

  const q = useQuery({
    queryKey: ["dxy-close-history", days, instrument],
    queryFn: () => fetchDxyCloseHistory(days, instrument),
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
  const hist = q.data?.history || [];

  const closes = hist.map((h) => h.close);
  const min = closes.length ? Math.min(...closes) : 0;
  const max = closes.length ? Math.max(...closes) : 1;
  const rng = (max - min) || 1;
  const first = hist[0], last = hist[hist.length - 1];
  const net = last && first ? last.close - first.close : 0;
  const netCls = net > 0 ? "text-pos" : net < 0 ? "text-neg" : "text-zinc-400";

  return (
    <div className="mt-5" data-testid="dxy-close-history">
      <div className="flex items-center gap-2 mb-2.5 text-[10px] uppercase tracking-widest text-zinc-600">
        <Select value={instrument} onValueChange={setInstrument}>
          <SelectTrigger data-testid="dxy-chart-instrument" className="h-6 w-[104px] bg-[#09090B] border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-300 px-2">
            <span className="truncate">{inst.label}</span>
          </SelectTrigger>
          <SelectContent className="dxy-archive-content bg-[#09090B] border-zinc-800">
            {INSTRUMENTS.map((x) => (
              <SelectItem key={x.key} value={x.key} className="text-[11px] font-mono text-zinc-300" data-testid={`dxy-chart-inst-${x.key}`}>
                {x.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="hidden sm:inline text-zinc-700">21:00 UTC</span>
        <span className="flex items-center gap-2 ml-2">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              data-testid={`dxy-range-${r}`}
              className={`font-mono transition-colors ${days === r ? "text-amber-term" : "text-zinc-600 hover:text-zinc-400"}`}
            >
              {r}D
            </button>
          ))}
        </span>
        {last && (
          <span className="ml-auto flex items-center gap-3 font-mono">
            <span className="text-zinc-500 hidden md:inline">H {fmt(max)} · L {fmt(min)}</span>
            <span className={netCls}>{days}D {net > 0 ? "+" : ""}{fmt(net)} ({((net / (first.close || 1)) * 100).toFixed(2)}%)</span>
          </span>
        )}
      </div>

      {hist.length < 2 ? (
        <div className="h-16 flex items-center text-[11px] uppercase tracking-widest text-zinc-700">Loading history…</div>
      ) : (
        <>
          <div className="flex items-stretch gap-1 h-16">
            {hist.map((h, i) => {
              const prev = i > 0 ? hist[i - 1].close : h.close;
              const up = h.close >= prev;
              const pct = 12 + ((h.close - min) / rng) * 88;
              const isLast = i === hist.length - 1;
              return (
                <div key={h.date} className="flex-1 flex flex-col justify-end group relative" data-testid={`dxy-close-bar-${h.date}`}>
                  <div className="pointer-events-none absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block z-20 whitespace-nowrap bg-[#09090B] border border-zinc-700 px-1.5 py-0.5 text-[9px] font-mono text-zinc-200 shadow-lg">
                    <span className="text-zinc-500">{h.date}</span> · <span className={up ? "text-pos" : "text-neg"}>{fmt(h.close)}</span>
                  </div>
                  <div
                    className={`w-full bg-current transition-opacity ${up ? "text-pos" : "text-neg"} ${isLast ? "opacity-100" : "opacity-45 group-hover:opacity-90"}`}
                    style={{ height: `${pct}%` }}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1.5 text-[9px] font-mono text-zinc-600 tabular-nums">
            <span>{first.date.slice(5)}</span>
            <span className="text-zinc-400">{last.date.slice(5)} · {fmt(last.close)}</span>
          </div>
        </>
      )}
    </div>
  );
}
